export const VOICE_MAX_DURATION_MS = 60_000;
export const VOICE_MAX_BYTES = 2 * 1024 * 1024;
export const VOICE_MIN_DURATION_MS = 450;
export const VOICE_BAR_COUNT = 12;

const MIME_CANDIDATES = [
  'audio/ogg;codecs=opus',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/aac',
] as const;

const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};

export type VoiceRecorderTick = {
  elapsedMs: number;
  levels: number[];
};

export type VoiceRecordingResult = {
  blob: Blob;
  mimeType: string;
  durationMs: number;
};

function audioContextCtor(): typeof AudioContext | null {
  const win = window as Window & { webkitAudioContext?: typeof AudioContext };
  return window.AudioContext || win.webkitAudioContext || null;
}

export function isVoiceCaptureSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    typeof MediaRecorder !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

export function pickRecorderMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';

  for (const type of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }

  return '';
}

export function toGeminiMimeType(mime: string): string {
  const base = mime.split(';')[0].trim().toLowerCase();
  if (base === 'audio/mp3') return 'audio/mpeg';
  if (base === 'audio/wave') return 'audio/wav';
  return base || 'audio/webm';
}

export function formatVoiceTimer(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler o áudio.'));
    reader.readAsDataURL(blob);
  });
}

export function idleVoiceLevels(): number[] {
  return Array.from({ length: VOICE_BAR_COUNT }, () => 0.16);
}

export class ChatbotVoiceRecorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private chunks: Blob[] = [];
  private mimeType = '';
  private startedAt = 0;
  private rafId = 0;
  private stopResolve: ((result: VoiceRecordingResult) => void) | null = null;
  private stopReject: ((error: Error) => void) | null = null;
  private cancelled = false;

  async start(onTick: (tick: VoiceRecorderTick) => void): Promise<void> {
    this.release();
    this.cancelled = false;

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: AUDIO_CONSTRAINTS,
      video: false,
    });

    try {
      const mimeType = pickRecorderMimeType();
      const recorder = this.createRecorder(stream, mimeType);

      this.stream = stream;
      this.recorder = recorder;
      this.mimeType = recorder.mimeType || mimeType || 'audio/webm';
      this.chunks = [];
      this.startedAt = Date.now();

      recorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) {
          this.chunks.push(event.data);
        }
      });

      recorder.addEventListener('error', (event) => {
        const error =
          (event as Event & { error?: Error }).error ??
          new Error('Falha ao gravar o áudio.');
        this.fail(error);
      });

      recorder.addEventListener('stop', () => {
        this.finish();
      });

      this.attachAnalyser(stream, onTick);
      recorder.start();
    } catch (error) {
      stream.getTracks().forEach((track) => track.stop());
      this.release();
      throw error;
    }
  }

  private createRecorder(stream: MediaStream, mimeType: string): MediaRecorder {
    const attempts: MediaRecorderOptions[] = [];
    if (mimeType) {
      attempts.push({ mimeType, audioBitsPerSecond: 16_000 });
      attempts.push({ mimeType });
    }
    attempts.push({ audioBitsPerSecond: 16_000 });
    attempts.push({});

    let lastError: unknown = null;
    for (const options of attempts) {
      try {
        return new MediaRecorder(stream, options);
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new Error('Seu navegador não conseguiu iniciar a gravação.');
  }

  stop(): Promise<VoiceRecordingResult> {
    if (!this.recorder || this.recorder.state === 'inactive') {
      this.release();
      return Promise.reject(new Error('Nenhuma gravação ativa.'));
    }

    return new Promise((resolve, reject) => {
      this.stopResolve = resolve;
      this.stopReject = reject;
      if (this.recorder?.state === 'recording') {
        this.recorder.stop();
      }
    });
  }

  cancel(): void {
    this.cancelled = true;
    this.stopResolve = null;
    this.stopReject = null;
    try {
      if (this.recorder?.state === 'recording') {
        this.recorder.stop();
      }
    } catch {
      // recorder já encerrado
    }
    this.release();
  }

  private attachAnalyser(
    stream: MediaStream,
    onTick: (tick: VoiceRecorderTick) => void
  ): void {
    const Ctor = audioContextCtor();
    if (!Ctor) return;

    const audioCtx = new Ctor();
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 64;
    analyser.smoothingTimeConstant = 0.42;

    const source = audioCtx.createMediaStreamSource(stream);
    source.connect(analyser);

    this.audioCtx = audioCtx;
    this.analyser = analyser;
    this.source = source;

    void audioCtx.resume();

    const bins = new Uint8Array(analyser.frequencyBinCount);

    const tick = () => {
      if (!this.analyser) return;
      this.analyser.getByteFrequencyData(bins);

      const levels = Array.from({ length: VOICE_BAR_COUNT }, (_, index) => {
        const start = Math.floor((index * bins.length) / VOICE_BAR_COUNT);
        const end = Math.max(
          start + 1,
          Math.floor(((index + 1) * bins.length) / VOICE_BAR_COUNT)
        );
        let sum = 0;
        for (let i = start; i < end; i += 1) {
          sum += bins[i] ?? 0;
        }
        const avg = sum / (end - start) / 255;
        return Math.max(0.12, Math.min(1, avg * 1.35));
      });

      onTick({
        elapsedMs: Date.now() - this.startedAt,
        levels,
      });

      this.rafId = requestAnimationFrame(tick);
    };

    this.rafId = requestAnimationFrame(tick);
  }

  private finish(): void {
    if (this.cancelled) {
      this.release();
      return;
    }

    const durationMs = Date.now() - this.startedAt;
    const mimeType = toGeminiMimeType(
      this.chunks[0]?.type || this.mimeType || 'audio/webm'
    );
    const blob = new Blob(this.chunks, { type: mimeType });
    const resolve = this.stopResolve;
    this.stopResolve = null;
    this.stopReject = null;
    this.release();

    resolve?.({ blob, mimeType, durationMs });
  }

  private fail(error: Error): void {
    const reject = this.stopReject;
    this.stopResolve = null;
    this.stopReject = null;
    this.release();
    reject?.(error);
  }

  private release(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }

    this.source?.disconnect();
    this.source = null;
    this.analyser = null;

    if (this.audioCtx) {
      void this.audioCtx.close();
      this.audioCtx = null;
    }

    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
  }
}
